/**
 * 模拟书签服务 - 前端演示版本
 * 使用localStorage模拟MongoDB操作，保持相同的API接口
 */

import { validateBookmark, createBookmark, updateBookmark, incrementClickCount } from '../models/Bookmark';

// 模拟MongoDB ObjectId
const generateObjectId = () => {
  return Date.now().toString(16) + Math.random().toString(16).substr(2, 8);
};

// 从localStorage获取数据
const getStorageData = () => {
  try {
    const data = localStorage.getItem('mongodb_bookmarks');
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('获取存储数据失败:', error);
    return [];
  }
};

// 保存数据到localStorage
const saveStorageData = (data) => {
  try {
    localStorage.setItem('mongodb_bookmarks', JSON.stringify(data));
  } catch (error) {
    console.error('保存存储数据失败:', error);
  }
};

/**
 * 模拟书签服务类 - 与真实MongoDB服务API保持一致
 */
class MockBookmarkService {
  /**
   * 获取用户的所有书签
   */
  static async getUserBookmarks(userId, options = {}) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allBookmarks = getStorageData();
        let userBookmarks = allBookmarks.filter(bookmark => bookmark.userId === userId);

        const {
          category = null,
          isPrivate = null,
          limit = 0,
          skip = 0,
          sortBy = 'createdAt',
          sortOrder = -1
        } = options;

        // 按分类过滤
        if (category) {
          userBookmarks = userBookmarks.filter(bookmark => bookmark.category === category);
        }

        // 按私有状态过滤
        if (isPrivate !== null) {
          userBookmarks = userBookmarks.filter(bookmark => bookmark.isPrivate === isPrivate);
        }

        // 排序
        userBookmarks.sort((a, b) => {
          const aValue = a[sortBy];
          const bValue = b[sortBy];
          
          if (aValue < bValue) return sortOrder === 1 ? -1 : 1;
          if (aValue > bValue) return sortOrder === 1 ? 1 : -1;
          return 0;
        });

        // 分页
        if (skip > 0) {
          userBookmarks = userBookmarks.slice(skip);
        }
        if (limit > 0) {
          userBookmarks = userBookmarks.slice(0, limit);
        }

        resolve(userBookmarks);
      }, 100); // 模拟网络延迟
    });
  }

  /**
   * 根据ID获取书签
   */
  static async getBookmarkById(bookmarkId, userId) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allBookmarks = getStorageData();
        const bookmark = allBookmarks.find(b => b._id === bookmarkId && b.userId === userId);
        resolve(bookmark || null);
      }, 50);
    });
  }

  /**
   * 创建新书签
   */
  static async createBookmark(bookmarkData, userId) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          // 创建书签对象
          const newBookmark = createBookmark(bookmarkData, userId);
          
          // 验证书签数据
          const validation = validateBookmark(newBookmark);
          if (!validation.isValid) {
            throw new Error(`数据验证失败: ${validation.errors.join(', ')}`);
          }

          const allBookmarks = getStorageData();
          
          // 检查是否已存在相同URL的书签
          const existingBookmark = allBookmarks.find(b => b.url === newBookmark.url && b.userId === userId);
          if (existingBookmark) {
            throw new Error('该网址的书签已存在');
          }

          // 添加_id
          const bookmarkWithId = {
            _id: generateObjectId(),
            ...newBookmark
          };

          allBookmarks.push(bookmarkWithId);
          saveStorageData(allBookmarks);
          
          resolve(bookmarkWithId);
        } catch (error) {
          reject(error);
        }
      }, 100);
    });
  }

  /**
   * 更新书签
   */
  static async updateBookmark(bookmarkId, updateData, userId) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          const allBookmarks = getStorageData();
          const bookmarkIndex = allBookmarks.findIndex(b => b._id === bookmarkId && b.userId === userId);
          
          if (bookmarkIndex === -1) {
            throw new Error('书签不存在');
          }

          const existingBookmark = allBookmarks[bookmarkIndex];
          const updatedBookmark = updateBookmark(existingBookmark, updateData);
          
          // 验证更新后的数据
          const validation = validateBookmark(updatedBookmark);
          if (!validation.isValid) {
            throw new Error(`数据验证失败: ${validation.errors.join(', ')}`);
          }

          // 检查URL重复
          if (updateData.url && updateData.url !== existingBookmark.url) {
            const duplicateBookmark = allBookmarks.find(b => 
              b.url === updateData.url && b.userId === userId && b._id !== bookmarkId
            );
            if (duplicateBookmark) {
              throw new Error('该网址的书签已存在');
            }
          }

          allBookmarks[bookmarkIndex] = updatedBookmark;
          saveStorageData(allBookmarks);
          
          resolve(updatedBookmark);
        } catch (error) {
          reject(error);
        }
      }, 100);
    });
  }

  /**
   * 删除书签
   */
  static async deleteBookmark(bookmarkId, userId) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allBookmarks = getStorageData();
        const initialLength = allBookmarks.length;
        const filteredBookmarks = allBookmarks.filter(b => !(b._id === bookmarkId && b.userId === userId));
        
        saveStorageData(filteredBookmarks);
        resolve(filteredBookmarks.length < initialLength);
      }, 100);
    });
  }

  /**
   * 批量删除书签
   */
  static async deleteBatchBookmarks(bookmarkIds, userId) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allBookmarks = getStorageData();
        const initialLength = allBookmarks.length;
        const filteredBookmarks = allBookmarks.filter(b => 
          !(bookmarkIds.includes(b._id) && b.userId === userId)
        );
        
        saveStorageData(filteredBookmarks);
        resolve(initialLength - filteredBookmarks.length);
      }, 100);
    });
  }

  /**
   * 搜索书签
   */
  static async searchBookmarks(userId, searchText, options = {}) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allBookmarks = getStorageData();
        let userBookmarks = allBookmarks.filter(bookmark => bookmark.userId === userId);

        const {
          category = null,
          limit = 50,
          skip = 0
        } = options;

        // 搜索过滤
        const lowerSearchText = searchText.toLowerCase();
        userBookmarks = userBookmarks.filter(bookmark =>
          bookmark.title.toLowerCase().includes(lowerSearchText) ||
          bookmark.description.toLowerCase().includes(lowerSearchText) ||
          bookmark.url.toLowerCase().includes(lowerSearchText) ||
          (bookmark.tags && bookmark.tags.some(tag => 
            tag.toLowerCase().includes(lowerSearchText)
          ))
        );

        // 按分类过滤
        if (category) {
          userBookmarks = userBookmarks.filter(bookmark => bookmark.category === category);
        }

        // 排序（按更新时间倒序）
        userBookmarks.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

        // 分页
        if (skip > 0) {
          userBookmarks = userBookmarks.slice(skip);
        }
        if (limit > 0) {
          userBookmarks = userBookmarks.slice(0, limit);
        }

        resolve(userBookmarks);
      }, 150);
    });
  }

  /**
   * 记录书签点击
   */
  static async recordBookmarkClick(bookmarkId, userId) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          const allBookmarks = getStorageData();
          const bookmarkIndex = allBookmarks.findIndex(b => b._id === bookmarkId && b.userId === userId);
          
          if (bookmarkIndex === -1) {
            throw new Error('书签不存在');
          }

          const bookmark = allBookmarks[bookmarkIndex];
          const updatedBookmark = incrementClickCount(bookmark);
          
          allBookmarks[bookmarkIndex] = updatedBookmark;
          saveStorageData(allBookmarks);
          
          resolve(updatedBookmark);
        } catch (error) {
          reject(error);
        }
      }, 50);
    });
  }

  /**
   * 获取书签统计信息
   */
  static async getBookmarkStats(userId) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const allBookmarks = getStorageData();
        const userBookmarks = allBookmarks.filter(bookmark => bookmark.userId === userId);

        const total = userBookmarks.length;
        const totalClicks = userBookmarks.reduce((sum, bookmark) => sum + (bookmark.clickCount || 0), 0);
        const avgClicksPerBookmark = total > 0 ? totalClicks / total : 0;

        // 分类统计
        const categoryStats = {};
        const categories = new Set();

        userBookmarks.forEach(bookmark => {
          const category = bookmark.category;
          categories.add(category);
          
          if (!categoryStats[category]) {
            categoryStats[category] = { count: 0, totalClicks: 0 };
          }
          categoryStats[category].count++;
          categoryStats[category].totalClicks += bookmark.clickCount || 0;
        });

        resolve({
          total,
          totalClicks,
          avgClicksPerBookmark,
          categoriesCount: categories.size,
          categoryStats
        });
      }, 100);
    });
  }

  /**
   * 导入默认书签
   */
  static async importDefaultBookmarks(defaultBookmarks, userId) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          const allBookmarks = getStorageData();
          const userBookmarks = allBookmarks.filter(bookmark => bookmark.userId === userId);
          
          if (userBookmarks.length > 0) {
            throw new Error('用户已有书签，无法导入默认书签');
          }

          // 转换默认书签格式
          const bookmarksToImport = defaultBookmarks.map(bookmark => ({
            _id: generateObjectId(),
            ...createBookmark(bookmark, userId)
          }));

          allBookmarks.push(...bookmarksToImport);
          saveStorageData(allBookmarks);
          
          resolve(bookmarksToImport.length);
        } catch (error) {
          reject(error);
        }
      }, 200);
    });
  }
}

export default MockBookmarkService; 